import { Controller } from '@hotwired/stimulus';
import { type ModalController } from './modal';

export class UserAccountController extends Controller {
    static outlets = ["modal"]
    declare readonly modalOutlets: ModalController[]
    static values = {
        mcpUrl: String,
    }

    static targets = [
        'cancelSubscriptionForm', 'cancelSubscriptionButtonLabel', 'cancelSubscriptionButtonLoading',
        'emailUnsubscribeForm', 'emailUnsubscribeButtonLabel', 'emailUnsubscribeButtonLoading',
        'emailSubscribeForm', 'emailSubscribeButtonLabel', 'emailSubscribeButtonLoading',
        'nameEditForm', 'emailEditForm', 'mcpInstallMessage', 'mcpSection',
        'deleteAccountForm', 'deleteAccountButtonLabel', 'deleteAccountButtonLoading'
    ];
    declare readonly cancelSubscriptionFormTarget: HTMLFormElement;
    declare readonly cancelSubscriptionButtonLabelTarget: HTMLElement;
    declare readonly cancelSubscriptionButtonLoadingTarget: HTMLElement;
    declare readonly emailUnsubscribeFormTarget: HTMLFormElement;
    declare readonly emailUnsubscribeButtonLabelTarget: HTMLElement;
    declare readonly emailUnsubscribeButtonLoadingTarget: HTMLElement;
    declare readonly emailSubscribeFormTarget: HTMLFormElement;
    declare readonly emailSubscribeButtonLabelTarget: HTMLElement;
    declare readonly emailSubscribeButtonLoadingTarget: HTMLElement;
    declare readonly nameEditFormTarget: HTMLElement;
    declare readonly emailEditFormTarget: HTMLElement;
    declare readonly mcpInstallMessageTarget: HTMLElement;
    declare readonly mcpSectionTarget: HTMLElement;
    declare readonly deleteAccountFormTarget: HTMLFormElement;
    declare readonly deleteAccountButtonLabelTarget: HTMLElement;
    declare readonly deleteAccountButtonLoadingTarget: HTMLElement;
    declare readonly mcpUrlValue: string;
    declare readonly hasMcpInstallMessageTarget: boolean;
    declare readonly hasMcpSectionTarget: boolean;


    connect(): void {
        document.addEventListener('ova:subscription:confirm-cancel', this.handleCancelConfirmed);
        document.addEventListener('ova:email:confirm-unsubscribe', this.handleEmailUnsubscribeConfirmed);
        document.addEventListener('ova:account:confirm-delete', this.handleAccountDeleteConfirmed);
    }

    disconnect(): void {
        document.removeEventListener('ova:subscription:confirm-cancel', this.handleCancelConfirmed);
        document.removeEventListener('ova:email:confirm-unsubscribe', this.handleEmailUnsubscribeConfirmed);
        document.removeEventListener('ova:account:confirm-delete', this.handleAccountDeleteConfirmed);
    }

    getModalById(id: string): ModalController {
        const modal = this.modalOutlets.find(modal => modal.element.id === id);
        if (!modal) {
            throw new Error(`Modal with id "${id}" not found`);
        }
        return modal;
    }

    handleCancelConfirmed = (): void => {
        try {
            this.cancelSubscriptionButtonLoadingTarget.classList.remove('hidden');
            this.cancelSubscriptionButtonLoadingTarget.classList.add('flex');
            this.cancelSubscriptionButtonLabelTarget.classList.add('hidden');
            this.cancelSubscriptionFormTarget.requestSubmit();
        } catch {
            console.warn('failed to cancel subscription');
        } finally {
            this.cancelSubscriptionButtonLabelTarget.classList.remove('hidden');
            this.cancelSubscriptionButtonLoadingTarget.classList.remove('flex');
            this.cancelSubscriptionButtonLoadingTarget.classList.add('hidden');
        }

    }

    handleEmailUnsubscribeConfirmed = (): void => {
        try {
            this.emailUnsubscribeButtonLoadingTarget.classList.remove('hidden');
            this.emailUnsubscribeButtonLoadingTarget.classList.add('flex');
            this.emailUnsubscribeButtonLabelTarget.classList.add('hidden');
            this.emailUnsubscribeFormTarget.requestSubmit();
        } catch {
            console.warn('failed to unsubscribe from emails');
        } finally {
            this.emailUnsubscribeButtonLabelTarget.classList.remove('hidden');
            this.emailUnsubscribeButtonLoadingTarget.classList.remove('flex');
            this.emailUnsubscribeButtonLoadingTarget.classList.add('hidden');
        }
    }

    handleAccountDeleteConfirmed = (): void => {
        try {
            this.deleteAccountButtonLoadingTarget.classList.remove('hidden');
            this.deleteAccountButtonLoadingTarget.classList.add('flex');
            this.deleteAccountButtonLabelTarget.classList.add('hidden');
            this.deleteAccountFormTarget.requestSubmit();
        } catch {
            console.warn('failed to delete account');
        } finally {
            this.deleteAccountButtonLabelTarget.classList.remove('hidden');
            this.deleteAccountButtonLoadingTarget.classList.remove('flex');
            this.deleteAccountButtonLoadingTarget.classList.add('hidden');
        }
    }
    
    toggleNameForm = (): void => {
        this.nameEditFormTarget.classList.toggle('hidden');
    }

    toggleEmailForm = (): void => {
        this.emailEditFormTarget.classList.toggle('hidden');
    }


    cancelSubscription = (): void => {
        console.log('Cancel subscription clicked');
        // Show the cancel confirmation modal
        this.getModalById('cancel-subscription-modal').show();
    }

    emailSubscribe = (): void => {
        try {
            this.emailSubscribeButtonLoadingTarget.classList.remove('hidden');
            this.emailSubscribeButtonLoadingTarget.classList.add('flex');
            this.emailSubscribeButtonLabelTarget.classList.add('hidden');
            this.emailSubscribeFormTarget.requestSubmit();
        } catch {
            console.warn('failed to subscribe to emails');
        } finally {
            this.emailSubscribeButtonLabelTarget.classList.remove('hidden');
            this.emailSubscribeButtonLoadingTarget.classList.remove('flex');
            this.emailSubscribeButtonLoadingTarget.classList.add('hidden');
        }
    }

    emailUnsubscribe = (): void => {
        console.log('Email unsubscribe clicked');
        // Show the email unsubscribe confirmation modal
        this.getModalById('unsubscribe-emails-modal').show();
    }

    deleteAccount = (): void => {
        console.log('Delete account clicked');
        // Show the account deletion confirmation modal
        this.getModalById('delete-account-modal').show();
    }

    autosavePreference = (event: Event): void => {
        const select = event.currentTarget as HTMLSelectElement | null;
        select?.form?.requestSubmit();
    }

    autosaveTimezone = this.autosavePreference

    copyMcpUrl = async (): Promise<void> => {
        try {
            await navigator.clipboard.writeText(this.mcpUrlValue);
            this.showMcpMessage(`Endpoint copied: ${this.mcpUrlValue}`);
        } catch {
            this.showMcpMessage(`Copy failed. Use this endpoint manually: ${this.mcpUrlValue}`);
        }
    }

    toggleMcpSection = (): void => {
        if (!this.hasMcpSectionTarget) {
            return;
        }
        this.mcpSectionTarget.classList.toggle('hidden');
    }

    private showMcpMessage(message: string): void {
        if (!this.hasMcpInstallMessageTarget) {
            return;
        }
        this.mcpInstallMessageTarget.textContent = `${message} Add it to your preferred MCP-compatible AI client.`;
        this.mcpInstallMessageTarget.classList.remove('hidden');
    }
}
